import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchApi, handleInjectInspectionTaskToModels } from "../../Functions";
import { addInspections, addSingleInspection } from "../../../store/Inspections";
import { addInspectionTask } from "../../../store/InspectionsTask";
import { addInspectionTaskLinks } from "../../../store/InspectionTaskLinks";
import { setLastFetchedInspectionDisciplineId } from "../../../store/Utilities";
import { RootState } from "../../../store/store";
import { URLs } from "../../../Constants/Consts";
import { toast } from "react-toastify";
import { TInspections } from "../../../types/types";
import { ModelApiItem } from "../../../types/Model/ModelSchema";

/**
 * Hook for fetching and managing inspection data from Redux
 * Handles initial data loading and provides computed/derived data
 */
export function useInspectionData(selectedModelNumber: string | null) {
    const dispatch = useDispatch();
    const { SelectedUser } = useSelector((state: RootState) => state.SelectedUser);
    const { Inspections } = useSelector((state: RootState) => state.Inspections);
    const { inspectionTask: InspectionTask } = useSelector((state: RootState) => state.InspectionTask);
    const { Models } = useSelector((state: RootState) => state.Model);
    const { inspectionTaskLinks } = useSelector((state: RootState) => state.InspectionTaskLinks);
    const { disciplineId } = useSelector((state: RootState) => state.SelectedUser.SelectedUser);
    const { lastFetchedInspectionDisciplineId } = useSelector((state: RootState) => state.Utilities);

    const [isLoading, setIsLoading] = useState(false);
    const [newlyCreatedInspection, setNewlyCreatedInspection] = useState<{ inspectionId: number; modelNumber: string } | null>(null);

    // Initial fetch on mount or when disciplineId changes
    useEffect(() => {
        if (!disciplineId || lastFetchedInspectionDisciplineId === disciplineId) return;

        dispatch(setLastFetchedInspectionDisciplineId(disciplineId));

        const fetchInitialData = async () => {
            setIsLoading(true);

            const fetchConfigs = [
                { url: URLs.getInspections(disciplineId), action: addInspections },
                { url: URLs.getInspectionTask(disciplineId), action: addInspectionTask },
                { url: URLs.getInspectionTaskLink(disciplineId), action: addInspectionTaskLinks },
            ];

            try {
                const fetchPromises = fetchConfigs.map(({ url, action }) =>
                    fetchApi(url)
                        .then(res => res.json())
                        .then(data => dispatch(action(data)))
                );
                await Promise.all(fetchPromises);
            } catch (error) {
                console.error("Error fetching inspection data:", error);
                toast.error("Failed to load inspection data");
            } finally {
                setIsLoading(false);
            }
        };

        fetchInitialData();
    }, [dispatch, disciplineId, lastFetchedInspectionDisciplineId]);

    // Inject inspection tasks into models for nested structure
    const injectedModelData = useMemo(
        () => handleInjectInspectionTaskToModels(Models, Inspections, InspectionTask, inspectionTaskLinks),
        [Models, Inspections, InspectionTask, inspectionTaskLinks]
    );

    // Get the current active model from injectedModelData
    const currentActiveModel = useMemo(() => {
        if (!selectedModelNumber) return null;
        return injectedModelData.find(m => m.modelNumber === selectedModelNumber) || null;
    }, [injectedModelData, selectedModelNumber]);

    // Get the active inspectionId
    const activeInspectionId = useMemo(() => {
        return currentActiveModel?.inspectionIds?.[0] || newlyCreatedInspection?.inspectionId || null;
    }, [currentActiveModel, newlyCreatedInspection]);

    // Add inspection to model
    const addInspectionToModel = async (model: ModelApiItem) => {
        const inspectionId = model?.inspectionId ? Number(model.inspectionId) : undefined;
        const payload = {
            inspectionId,
            disciplineId: SelectedUser?.disciplineId,
            modelNumber: model?.modelNumber,
            inspectionDescription: "",
            inspectionVersion: new Date().toISOString().split("T")[0]
        };
        try {
            const res = await fetchApi(URLs.addInspection(), {
                method: "POST",
                body: JSON.stringify(payload),
                headers: { 'Content-Type': 'application/json' }
            });
            const inspection = await res.json();

            toast.success(inspection?.message || "Inspection added to Model successfully");

            const newInspection: TInspections = {
                inspectionId: inspection?.itemId,
                disciplineId: SelectedUser?.disciplineId,
                modelNumber: model?.modelNumber,
                inspectionDescription: "",
                inspectionVersion: payload.inspectionVersion
            };
            dispatch(addSingleInspection(newInspection));

            const createdInspection = {
                inspectionId: inspection?.itemId,
                modelNumber: model?.modelNumber
            };
            setNewlyCreatedInspection(createdInspection);
            return { success: true, inspection: createdInspection };
        } catch (error) {
            const message = error instanceof Error ? error.message : "Failed to add Inspection to Model";
            toast.error(message);
            return { success: false, inspection: null };
        }
    };

    const clearNewlyCreatedInspection = () => setNewlyCreatedInspection(null);

    return {
        isLoading,
        injectedModelData,
        currentActiveModel,
        activeInspectionId,
        newlyCreatedInspection,
        addInspectionToModel,
        clearNewlyCreatedInspection,
        // Expose raw Redux data for fresh lookups
        inspectionTaskLinks,
    };
}
